import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

// Account-type filter for the Accounts tab — a tick menu, one row per account
// type, unticking hides that type from the table.
//
// Deliberately self-contained rather than a refactor of AlertsViewer's
// AlertTypeFilter: the behaviour is copied (outside-click close, capture-phase
// Escape) but this one has three fixed options and no search, and keeping it
// separate leaves the alerts viewer untouched.
//
// The selection applies to EVERY Accounts view (Default, Usage, Billing) —
// switching view must never change which accounts are on screen.

// Display order and labels. These match the Type column in the Default view.
export const ACCOUNT_TYPES = [
  { key: 'freeHearthOwningAccount', label: 'Free Hearth' },
  { key: 'payingHearthOwningAccount', label: 'Paying Hearth' },
  { key: 'invitedByCustomerAccount', label: 'Invited' },
];

// Anything unrecognised (or missing) is bucketed here, so a new or malformed
// account type can never become invisible AND unfilterable.
export const OTHER_ACCOUNT_TYPE = '__other__';

/** Normalise an account's raw accountType to one of the filter keys. */
export function accountTypeKey(accountType) {
  return ACCOUNT_TYPES.some((t) => t.key === accountType) ? accountType : OTHER_ACCOUNT_TYPE;
}

/** Is this account visible under the given hidden-type map? */
export function isAccountTypeVisible(account, hidden) {
  if (!hidden) return true;
  return !hidden[accountTypeKey(account?.accountType)];
}

// Small switch visual, matching the one in the alerts type filter.
function SwitchVisual({ checked }) {
  return (
    <span
      className={`inline-flex h-4 w-7 flex-shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-blue-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`h-3 w-3 rounded-full bg-white transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </span>
  );
}

/**
 * counts   — { [typeKey]: number } over ALL accounts
 * hidden   — { [typeKey]: true } (empty = show everything)
 * onToggle — (typeKey) => void
 * onShowAll— () => void
 * onHideAll— () => void
 */
export default function AccountTypeFilter({ counts = {}, hidden = {}, onToggle, onShowAll, onHideAll }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // While open: close on outside mousedown, and on Escape in the capture phase
  // so it wins over any document-level Escape handler further up the tree.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyCapture = (e) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKeyCapture, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKeyCapture, true);
    };
  }, [open]);

  // Only offer the "Other" row when such accounts actually exist, so the menu
  // stays three rows in the normal case.
  const options = [
    ...ACCOUNT_TYPES,
    ...(counts[OTHER_ACCOUNT_TYPE] > 0 ? [{ key: OTHER_ACCOUNT_TYPE, label: 'Other' }] : []),
  ];
  const hiddenCount = options.filter((o) => hidden[o.key]).length;

  return (
    <div ref={rootRef} className="relative w-[150px]">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title="Show or hide account types across all Accounts views"
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        <span className="line-clamp-1 text-left">
          Account types{hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}
        </span>
        <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
      </button>
      {open && (
        <div
          role="group"
          aria-label="Filter account types"
          className="absolute z-50 mt-1 w-max min-w-full max-w-[320px] rounded-md border bg-popover text-popover-foreground shadow-md p-1"
        >
          <div className="flex items-center justify-between gap-3 px-2 py-1.5">
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {hiddenCount === 0 ? 'All types shown' : `${hiddenCount} hidden`}
            </span>
            {hiddenCount === 0 ? (
              <button type="button" onClick={onHideAll} className="text-xs text-blue-600 hover:underline whitespace-nowrap">
                Hide all
              </button>
            ) : (
              <button type="button" onClick={onShowAll} className="text-xs text-blue-600 hover:underline whitespace-nowrap">
                Show all
              </button>
            )}
          </div>
          <div className="-mx-1 my-1 h-px bg-muted" />
          {options.map((o) => {
            const visible = !hidden[o.key];
            return (
              <button
                type="button"
                key={o.key}
                role="switch"
                aria-checked={visible}
                aria-label={o.label}
                onClick={() => onToggle(o.key)}
                className="flex w-full items-center gap-2 rounded-sm py-1.5 px-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
              >
                <SwitchVisual checked={visible} />
                <span className={`flex-1 min-w-0 text-left truncate ${visible ? '' : 'text-muted-foreground line-through'}`}>
                  {o.label}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums flex-shrink-0">{counts[o.key] || 0}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
